import {
  Edit,
  ExternalLink,
  FolderOpen,
  Hash,
  Link2,
  Mail,
  MoreVertical,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ClientProfileWithCaseCount } from '../../types/case-management'
import { StatusBadge } from './StatusBadge'

interface ActionMenuProps {
  client: ClientProfileWithCaseCount
  isAdmin: boolean
  onEdit: (client: ClientProfileWithCaseCount) => void
  onLink: (client: ClientProfileWithCaseCount) => void
  onDelete: (client: ClientProfileWithCaseCount) => void
}

function ActionMenu(props: ActionMenuProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { client, isAdmin, onEdit, onLink, onDelete } = props
  return (
    <div className="absolute right-0 z-50 mt-1 w-48 rounded-lg border border-stone-200 bg-white py-1 shadow-lg dark:border-stone-700 dark:bg-stone-800">
      <button
        onClick={() => {
          onEdit(client)
        }}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <Edit className="h-4 w-4" />
        {t('clients.edit')}
      </button>
      <button
        onClick={() => {
          onLink(client)
        }}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <Link2 className="h-4 w-4" />
        {t('clients.linkToCase')}
      </button>
      {(client.caseCount === 0 || isAdmin) && (
        <button
          onClick={() => {
            onDelete(client)
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
        >
          <Trash2 className="h-4 w-4" />
          {t('actions.delete')}
        </button>
      )}
    </div>
  )
}

function ClientNameCell({ client }: { client: ClientProfileWithCaseCount }): React.ReactElement {
  return (
    <td className="px-6 py-4">
      <div>
        <div className="font-medium text-stone-900 dark:text-stone-100">{client.name}</div>
        {client.shortName && (
          <div className="text-sm text-stone-500 dark:text-stone-400">{client.shortName}</div>
        )}
        {client.referenceCode && (
          <div className="flex items-center gap-1 text-xs text-stone-400 dark:text-stone-500">
            <Hash className="h-3 w-3" />
            {client.referenceCode}
          </div>
        )}
      </div>
    </td>
  )
}

function ContactCell({ client }: { client: ClientProfileWithCaseCount }): React.ReactElement {
  if (!client.primaryContactName) {
    return (
      <td className="px-6 py-4">
        <span className="text-sm text-stone-400 dark:text-stone-500">-</span>
      </td>
    )
  }
  return (
    <td className="px-6 py-4">
      <div className="text-sm">
        <div className="text-stone-900 dark:text-stone-100">{client.primaryContactName}</div>
        {client.primaryContactEmail && (
          <div className="flex items-center gap-1 text-stone-500 dark:text-stone-400">
            <Mail className="h-3 w-3" />
            {client.primaryContactEmail}
          </div>
        )}
      </div>
    </td>
  )
}

interface CaseCountCellProps {
  client: ClientProfileWithCaseCount
  onView: (clientId: string) => void
}

function CaseCountCell({ client, onView }: CaseCountCellProps): React.ReactElement {
  if (client.caseCount === 0) {
    return (
      <td className="px-6 py-4">
        <div className="flex items-center gap-1 text-sm">
          <FolderOpen className="h-4 w-4 text-stone-400" />
          <span className="font-medium text-stone-500 dark:text-stone-400">0</span>
        </div>
      </td>
    )
  }
  const plural = client.caseCount === 1 ? '' : 's'
  return (
    <td className="px-6 py-4">
      <button
        onClick={() => {
          onView(client.id)
        }}
        className="group flex items-center gap-1 text-sm transition-colors hover:text-[#5c6e94] dark:hover:text-[#8a9bba]"
        title={`View ${String(client.caseCount)} case${plural} for ${client.name}`}
      >
        <FolderOpen className="h-4 w-4 text-stone-400 group-hover:text-[#4e5e82]" />
        <span className="font-medium text-stone-900 underline decoration-dotted underline-offset-2 group-hover:text-[#5c6e94] dark:text-stone-100 dark:group-hover:text-[#8a9bba]">
          {client.caseCount}
        </span>
        <ExternalLink className="h-3 w-3 text-stone-400 opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
    </td>
  )
}

export interface ClientRowProps {
  client: ClientProfileWithCaseCount
  isAdmin: boolean
  actionMenuClient: string | null
  setActionMenuClient: (id: string | null) => void
  onEdit: (client: ClientProfileWithCaseCount) => void
  onLink: (client: ClientProfileWithCaseCount) => void
  onDelete: (client: ClientProfileWithCaseCount) => void
  onViewCases: (clientId: string) => void
}

export function ClientRow(props: ClientRowProps): React.ReactElement {
  const { client, isAdmin, actionMenuClient, setActionMenuClient } = props
  const { onEdit, onLink, onDelete, onViewCases } = props
  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-800/50">
      <ClientNameCell client={client} />
      <td className="px-6 py-4 text-sm text-stone-500 dark:text-stone-400">
        {client.industry ?? '-'}
      </td>
      <ContactCell client={client} />
      <CaseCountCell client={client} onView={onViewCases} />
      <td className="px-6 py-4">
        <StatusBadge status={client.status} />
      </td>
      <td className="px-6 py-4 text-right">
        <div className="relative inline-block">
          <button
            onClick={() => {
              setActionMenuClient(actionMenuClient === client.id ? null : client.id)
            }}
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600 dark:hover:bg-stone-800 dark:hover:text-stone-300"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
          {actionMenuClient === client.id && (
            <ActionMenu
              client={client}
              isAdmin={isAdmin}
              onEdit={onEdit}
              onLink={onLink}
              onDelete={onDelete}
            />
          )}
        </div>
      </td>
    </tr>
  )
}
